/* Iceberg UI */
import { Box, Button, Flex, Flyout } from '@auviknetworks/iceberg-ui';
import { Editor, Monaco } from '@monaco-editor/react';

/* Shared Components */
import { PageTitle } from 'application/Shared/Components/App';
import { UserGreeting } from 'application/Shared/Components/App/UserGreeting';
import { Dashboard } from 'application/Shared/Components/Dashboard';
import {
    basicPanelSchema,
    chartPanelSchema,
    contextSchema,
    customPanelSchema,
    dashboardSchema,
    dataSourceSchema,
    entityPanelSchema,
    footerSchema,
    groupPanelSchema,
    kpiPanelSchema,
    layoutSchema,
    metaSchema,
    panelSchema,
    panelsSchema,
    tablePanelSchema
} from 'application/Shared/Components/Dashboard/schemas';
import type { Dashboard as DashboardType } from 'application/Shared/Components/Dashboard/types';
import { TenantPicker } from 'application/Shared/Components/Pickers';
import { Z_INDEX } from 'application/Shared/Constants';
import { TenantProvider } from 'application/Shared/Contexts/TenantContext';

/* Hooks */
import { useUserTheme } from 'hooks/dataAccess/User/userTheme';

/* React */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import exampleDashboard from './example.json';

type SchemaEditorProps = {
    schema: DashboardType;
    onUpdate: (schema: DashboardType) => void;
};

const configureEditor = (monaco: Monaco) => {
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        schemas: [
            // We give Monaco all the schemas context
            {
                fileMatch: ['*'],
                schema: dashboardSchema,
                uri: 'https://auvik.com/schemas/asm/dashboard/dashboard.schema.json'
            },
            {
                schema: customPanelSchema,
                uri: 'https://auvik.com/schemas/asm/dashboard/panels/customPanel.schema.json'
            },
            {
                schema: basicPanelSchema,
                uri: 'https://auvik.com/schemas/asm/dashboard/panels/basicPanel.schema.json'
            },
            {
                schema: kpiPanelSchema,
                uri: 'https://auvik.com/schemas/asm/dashboard/panels/kpiPanel.schema.json'
            },
            {
                schema: chartPanelSchema,
                uri: 'https://auvik.com/schemas/asm/dashboard/panels/chartPanel.schema.json'
            },
            {
                schema: tablePanelSchema,
                uri: 'https://auvik.com/schemas/asm/dashboard/panels/tablePanel.schema.json'
            },
            {
                schema: entityPanelSchema,
                uri: 'https://auvik.com/schemas/asm/dashboard/panels/entityPanel.schema.json'
            },
            {
                schema: groupPanelSchema,
                uri: 'https://auvik.com/schemas/asm/dashboard/panels/groupPanel.schema.json'
            },
            {
                schema: layoutSchema,
                uri: 'https://auvik.com/schemas/asm/dashboard/common/layout.schema.json'
            },
            {
                schema: metaSchema,
                uri: 'https://auvik.com/schemas/asm/dashboard/common/meta.schema.json'
            },
            {
                schema: panelSchema,
                uri: 'https://auvik.com/schemas/asm/dashboard/common/panel.schema.json'
            },
            {
                schema: dataSourceSchema,
                uri: 'https://auvik.com/schemas/asm/dashboard/common/dataSource.schema.json'
            },
            {
                schema: footerSchema,
                uri: 'https://auvik.com/schemas/asm/dashboard/common/footer.schema.json'
            },
            {
                schema: contextSchema,
                uri: 'https://auvik.com/schemas/asm/dashboard/common/context.schema.json'
            },
            {
                schema: panelsSchema,
                uri: 'https://auvik.com/schemas/asm/dashboard/panels/panels.schema.json'
            }
        ],
        schemaValidation: 'error',
        validate: true
    });
};

const SchemaEditor = ({ schema, onUpdate }: SchemaEditorProps) => {
    const userTheme = useUserTheme();

    const handleEditorChange = (value: string | undefined) => {
        if (value === undefined) return;

        try {
            const parsedContent = JSON.parse(value);
            onUpdate(parsedContent as DashboardType);
        } catch (error) {
            console.error('Failed to parse JSON:', error);
        }
    };

    return (
        <Flyout title="Schema" width="640px" zIndex={Z_INDEX.FLYOUT_LOW}>
            <Editor
                height="100%"
                defaultLanguage="json"
                theme={userTheme === 'dark' ? 'vs-dark' : 'vs-light'}
                beforeMount={configureEditor}
                value={JSON.stringify(schema, null, 2)}
                onChange={handleEditorChange}
                options={{
                    scrollBeyondLastLine: true
                }}
            />
        </Flyout>
    );
};

/**
 * Global Admin Dashboard playground
 */
export const Playground = () => {
    const { t } = useTranslation();
    const [schema, setSchema] = useState<DashboardType>(
        exampleDashboard as unknown as DashboardType
    );
    const [isEditing, setIsEditing] = useState(false);
    const [tenantId, setTenantId] = useState<string | null>(null);

    return (
        <>
            <Box sx={{ marginBottom: '16px' }}>
                <UserGreeting />
            </Box>
            <Flex alignItems="start" sx={{ gap: '8px' }}>
                <PageTitle title={t('Admin.Dashboard.Playground.title')} />
                <Button
                    data-trackid="globalAdmin_dashboard_saveEdit"
                    onClick={() => setIsEditing(!isEditing)}
                    size="small"
                    variant="secondary"
                >
                    {isEditing ? t('Common.save') : t('Common.edit')}
                </Button>
                <TenantPicker
                    allValue={t('Common.choose_a_client')}
                    allValueId="0"
                    value={tenantId}
                    size="sm"
                    variant="outlined"
                    color="secondary"
                    onChange={(value: string) => setTenantId(value)}
                />
            </Flex>

            <Flex width="100%">
                <TenantProvider clientId={tenantId}>
                    <Dashboard
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        handleLayoutChange={(layout: any) =>
                            // Only update the layout if the dashboard is not being edited, otherwise filtering will override the layout.
                            isEditing ? setSchema({ ...schema, layout }) : null
                        }
                        setSchema={(schema) =>
                            setSchema(schema as DashboardType)
                        }
                        isEditing={isEditing}
                        dashboard={schema}
                    />
                </TenantProvider>
            </Flex>
            {isEditing && <SchemaEditor schema={schema} onUpdate={setSchema} />}
        </>
    );
};
